import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';

// Lazy load views for code splitting
const HandwryttenExportView = lazy(() => import('@pages/handwrytten/HandwryttenExportView').then(m => ({ default: m.HandwryttenExportView })));
const LabelGeneratorView = lazy(() => import('@pages/labels/LabelGeneratorView').then(m => ({ default: m.LabelGeneratorView })));
const ContactManagementView = lazy(() => import('@pages/contacts/ContactManagementView').then(m => ({ default: m.ContactManagementView })));
const ReviewRoutesView = lazy(() => import('@pages/routes/ReviewRoutesView').then(m => ({ default: m.ReviewRoutesView })));
const AddressManagementView = lazy(() => import('@pages/addresses/AddressManagementView').then(m => ({ default: m.AddressManagementView })));
const OrganizationManagementView = lazy(() => import('@pages/organizations/OrganizationManagementView').then(m => ({ default: m.OrganizationManagementView })));
const CampaignManagementView = lazy(() => import('@pages/campaigns/CampaignManagementView').then(m => ({ default: m.CampaignManagementView })));

// New Page Architecture - use page components from src/pages/
const DashboardView = lazy(() => import('@pages/dashboard/DashboardView').then(m => ({ default: m.DashboardView })));
const RouteView = lazy(() => import('@pages/routes/RouteView').then(m => ({ default: m.RouteView })));
const ExecutionTrackingView = lazy(() => import('@pages/execution/ExecutionTrackingView').then(m => ({ default: m.ExecutionTrackingView })));
const NotificationsPage = lazy(() => import('@pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const GeocodingInfoPage = lazy(() => import('@pages/notifications/GeocodingInfoPage').then(m => ({ default: m.GeocodingInfoPage })));

// Detail Views (Phase 3)
const OrganizationDetailView = lazy(() => import('@pages/organizations/OrganizationDetailView').then(m => ({ default: m.OrganizationDetailView })));
const CampaignDetailView = lazy(() => import('@pages/campaigns/CampaignDetailView').then(m => ({ default: m.CampaignDetailView })));
const ContactDetailView = lazy(() => import('@pages/contacts/ContactDetailView').then(m => ({ default: m.ContactDetailView })));
const RouteDetailView = lazy(() => import('@pages/routes/RouteDetailView').then(m => ({ default: m.RouteDetailView })));
const AddressDetailView = lazy(() => import('@pages/addresses/AddressDetailView').then(m => ({ default: m.AddressDetailView })));

// EPG (Event Planning Grid)
const EPGPage = lazy(() => import('@features/epg').then(m => ({ default: m.EPGPage })));
const EPGCalendarView = lazy(() => import('@features/epg/pages/EPGCalendarView').then(m => ({ default: m.EPGCalendarView })));
const EPGEventDetailView = lazy(() => import('@features/epg/pages/EPGEventDetailView').then(m => ({ default: m.EPGEventDetailView })));

// Drop Follow-Ups
const DropFollowUpsView = lazy(() => import('@pages/follow-ups/DropFollowUpsView').then(m => ({ default: m.DropFollowUpsView })));

// Weekly Reports
const WeeklyDropReportView = lazy(() => import('@pages/reports/WeeklyDropReportView').then(m => ({ default: m.WeeklyDropReportView })));
const WeeklyCallStatusView = lazy(() => import('@pages/reports/WeeklyCallStatusView').then(m => ({ default: m.WeeklyCallStatusView })));

// Admin - Field Management
const FieldManagementLayout = lazy(() => import('@features/admin').then(m => ({ default: m.FieldManagementLayout })));

// Notes Module
const NotesContent = lazy(() => import('@features/notes').then(m => ({ default: m.NotesContent })));
const WeeklyTaskBoard = lazy(() => import('@features/notes/components/WeeklyTaskBoard').then(m => ({ default: m.WeeklyTaskBoard })));

// Vibe Remote - Claude Code Session Monitor
const HappyDashboard = lazy(() => import('@features/happy').then(m => ({ default: m.HappyDashboard })));
const TerminalConnect = lazy(() => import('@features/happy/components/TerminalConnect').then(m => ({ default: m.TerminalConnect })));
const PTYOnlyView = lazy(() => import('@features/happy/components/PTYOnlyView').then(m => ({ default: m.PTYOnlyView })));

// CLI Dashboard - Remote CLI Session Management
const CLIDashboard = lazy(() => import('@features/cli-dashboard').then(m => ({ default: m.CLIDashboard })));

// PUG Records - Real-time sync with PUG desktop
const PugRecordsPage = lazy(() => import('@pages/pug/PugRecordsPage').then(m => ({ default: m.PugRecordsPage })));

// Expenses - Receipt upload with OCR
const ExpensesPage = lazy(() => import('@pages/expenses/ExpensesPage').then(m => ({ default: m.ExpensesPage })));

// Planka - Self-hosted Kanban board
const PlankaEmbedView = lazy(() => import('@pages/planka/PlankaEmbedView').then(m => ({ default: m.PlankaEmbedView })));

// Houston Marketing Calendar
const CalendarPage = lazy(() => import('@pages/calendar/CalendarPage').then(m => ({ default: m.CalendarPage })));

// HMC Events - Event Report Viewer
const HMCEventsPage = lazy(() => import('@pages/hmc-events/HMCEventsPage').then(m => ({ default: m.HMCEventsPage })));

// Client Timelines - Timeline Manager
const TimelinesPage = lazy(() => import('@pages/timelines/TimelinesPage').then(m => ({ default: m.TimelinesPage })));

// Ops Monitor - Live activity dashboard
const ClawdBotDashboard = lazy(() => import('@pages/clawd-bot/ClawdBotDashboard').then(m => ({ default: m.ClawdBotDashboard })));

// Flashcards - Study workspace
const FlashcardsPage = lazy(() => import('@pages/flashcards/FlashcardsPage').then(m => ({ default: m.FlashcardsPage })));

// Calendar Availability - Outlook FreeBusy Dashboard
const AvailabilityView = lazy(() => import('@pages/availability/AvailabilityView').then(m => ({ default: m.AvailabilityView })));

// HMC Guest View - Shareable link access (no auth required)
const HMCGuestPage = lazy(() => import('@pages/hmc-events/HMCGuestPage').then(m => ({ default: m.HMCGuestPage })));

// OMNI Report Generator - Underwriting reports for 15 DP opportunities
const ReportGeneratorView = lazy(() => import('@pages/report-generator').then(m => ({ default: m.ReportGeneratorView })));

// File Inbox - Email attachments from Gmail polling
const FileInboxPage = lazy(() => import('@pages/file-inbox/FileInboxPage').then(m => ({ default: m.FileInboxPage })));

// Email Invitations - Mass email campaign manager for cenari.net
const EmailInvitationsPage = lazy(() => import('@pages/email-invitations/EmailInvitationsPage').then(m => ({ default: m.EmailInvitationsPage })));

// Inbox - Outlook inbox monitoring
const InboxPage = lazy(() => import('@pages/email-notifications/EmailNotificationsPage').then(m => ({ default: m.InboxPage })));

// USI Events - Event-driven email campaigns with postcard attachments
const USIEventsPage = lazy(() => import('@pages/usi-events/USIEventsPage').then(m => ({ default: m.USIEventsPage })));

// USI Events Early RSVP - Public form for early event interest registration
const EarlyRSVPPage = lazy(() => import('@pages/usi-events/EarlyRSVPPage').then(m => ({ default: m.EarlyRSVPPage })));

// File Manager - File storage with folders and screenshots
const FileManagerPage = lazy(() => import('@pages/file-manager').then(m => ({ default: m.FileManagerPage })));

// Boards Home - Weekly Planning Boards landing page
const BoardsHomePage = lazy(() => import('@features/weekly-board').then(m => ({ default: m.BoardsHome })));

// Opportunities Analyzer - OMNI vs Pipedrive comparison
const OpportunitiesAnalyzerPage = lazy(() => import('@pages/opportunities-analyzer').then(m => ({ default: m.OpportunitiesAnalyzerPage })));

// Business Plan - Campaign business plan management
const BusinessPlanView = lazy(() => import('@features/business-plan/pages/BusinessPlanView').then(m => ({ default: m.BusinessPlanView })));

/**
 * Guest Portal Subdomain Detection (HMC Events)
 *
 * Detects if the app is being accessed from the dedicated guest portal subdomain
 * (usibenefits-events.eroland.me) and returns true if so.
 *
 * On the subdomain, the entire app functions as the guest portal:
 * - Root path (/) shows a welcome/info page
 * - /:shareId routes directly to the guest event view
 */
const isGuestPortalSubdomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'usibenefits-events.eroland.me';
};

/**
 * Events Portal Subdomain Detection (USI Events / Early RSVP)
 *
 * Detects if the app is being accessed from the dedicated events subdomain
 * (events.eroland.me) for USI Events early RSVP registration.
 *
 * On this subdomain:
 * - /early-rsvp routes to the Early RSVP form
 * - Root path (/) shows a welcome/info page for events
 */
const isEventsSubdomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'events.eroland.me';
};

/**
 * Guest Portal Root Handler
 *
 * When accessed from usibenefits-events.eroland.me:
 * - If path is just "/", show a welcome page with branding
 * - If path is "/:shareId", treat it as a share token and show guest view
 */
const GuestPortalRoot: React.FC = () => {
  // On the guest subdomain, the root path shows a welcome/landing page
  // Guests with valid tokens access via /{token} which is handled by HMC_GUEST_DIRECT route
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-white truncate">USI Benefits Events</h1>
          <p className="text-dark-muted">
            Welcome to the USI Benefits event portal.
          </p>
        </div>
        <div className="bg-dark-surface/50 rounded-lg p-6 space-y-4">
          <p className="text-dark-text text-sm">
            If you received an event invitation link, please use the full URL from your email to access your personalized event details.
          </p>
          <p className="text-dark-muted text-xs">
            Need help? Contact your USI Benefits representative.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Guest Portal Direct Access Handler
 *
 * Handles /:shareId routes on the guest subdomain.
 * Extracts the share token from the URL and renders HMCGuestPage.
 */
const GuestPortalDirect: React.FC = () => {
  // Get the shareId from the URL path (everything after the leading slash)
  const shareId = window.location.pathname.slice(1);

  if (!shareId) {
    // No token - show root page
    return <GuestPortalRoot />;
  }

  // Render guest page with the share token
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <HMCGuestPage />
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Events Portal Root Handler
 *
 * Welcome page for events.eroland.me subdomain.
 * Users typically access /early-rsvp?events=... directly from email links.
 */
const EventsPortalRoot: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#1a365d' }}>USI Benefits Events</h1>
          <p className="text-dark-muted">
            Your gateway to exclusive USI Benefits events.
          </p>
        </div>
        <div className="bg-dark-surface/95 backdrop-blur-lg rounded-xl shadow-2xl shadow-black/25 p-6 space-y-4 border border-dark-border/30">
          <p className="text-dark-text text-sm">
            If you received an event invitation link, please use the full URL from your email to register your interest.
          </p>
          <p className="text-dark-muted text-xs">
            Need help? Contact your USI Benefits representative.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Events Portal Route Handler
 *
 * Handles routes on the events.eroland.me subdomain.
 * - /early-rsvp renders the Early RSVP form
 * - / renders the welcome page
 */
const EventsPortalHandler: React.FC = () => {
  const pathname = window.location.pathname;

  // Handle /early-rsvp route
  if (pathname === '/early-rsvp' || pathname.startsWith('/early-rsvp?')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <EarlyRSVPPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Root path - show welcome page
  return <EventsPortalRoot />;
};

// Static imports for auth pages (critical for first paint)
import { LandingPage } from '../../views/LandingPage';
import { LoginPage } from '../../views/LoginPage';
import { PricingPage } from '../../views/PricingPage';

// App Shell (authenticated layout)
import { AppShell } from '@shared/components/layouts/AppShell';

// Error boundary
import { ErrorBoundary } from '@shared/components/ErrorBoundary';

// Landing page configuration
import landingPageConfig from '@/config/landingPage.json';

// Auth store for authentication checking
import { useAuthStore } from '@shared/store/authStore';
import { useSelfHostedAuthStore } from '@features/happy/store/selfHostedAuthStore';
import { hasAuthCookies } from '@features/happy/services/happyAuth';

/**
 * Route path constants for type-safe navigation
 *
 * Includes both static routes and parameterized route patterns.
 * Use the provided helper functions to build URLs with parameters.
 */
export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  PRICING: '/pricing',

  // Authenticated routes (app)
  APP: '/app',
  DASHBOARD: '/app/dashboard',

  /** Campaign Management routes */
  CAMPAIGNS: '/app/campaigns',
  /** Campaign detail view - Parameters: campaignId (number) */
  CAMPAIGN_DETAIL: '/app/campaigns/:campaignId',
  /** Campaign edit view - Parameters: campaignId (number) */
  CAMPAIGN_EDIT: '/app/campaigns/:campaignId/edit',
  /** Campaign business plan view - Parameters: campaignId (number) */
  CAMPAIGN_BUSINESS_PLAN: '/app/campaigns/:campaignId/business-plan',

  /** Organization Management routes */
  ORGANIZATIONS: '/app/organizations',
  /** Organization detail view - Parameters: organizationId (number) */
  ORGANIZATION_DETAIL: '/app/organizations/:organizationId',
  /** Organization edit view - Parameters: organizationId (number) */
  ORGANIZATION_EDIT: '/app/organizations/:organizationId/edit',

  /** Address Management routes */
  ADDRESSES: '/app/addresses',
  /** Address detail view - Parameters: addressId (number) */
  ADDRESS_DETAIL: '/app/addresses/:addressId',

  /** Route Planning routes */
  ROUTES: '/app/routes',
  /** Route detail view - Parameters: routeId (string) */
  ROUTE_DETAIL: '/app/routes/:routeId',
  /** Route edit view - Parameters: routeId (string) */
  ROUTE_EDIT: '/app/routes/:routeId/edit',
  /** Route stop detail - Parameters: routeId (string), stopId (number) */
  ROUTE_STOP_DETAIL: '/app/routes/:routeId/stops/:stopId',

  /** Review Routes */
  REVIEW_ROUTES: '/app/review-routes',

  /** Contact Management routes */
  CONTACTS: '/app/contacts',
  /** Contact detail view - Parameters: contactId (number) */
  CONTACT_DETAIL: '/app/contacts/:contactId',

  /** Label Generator */
  LABELS: '/app/labels',

  /** Execution Tracking routes */
  EXECUTION: '/app/execution',
  /** Execution detail view - Parameters: executionId (number) */
  EXECUTION_DETAIL: '/app/execution/:executionId',

  /** Handwrytten Export */
  HANDWRYTTEN: '/app/handwrytten',

  /** Event Planning Grid routes */
  EPG: '/app/epg',
  /** EPG calendar view - Parameters: year (number), month (number) */
  EPG_CALENDAR: '/app/epg/:year/:month',
  /** EPG event detail - Parameters: eventId (number) */
  EPG_EVENT_DETAIL: '/app/epg/events/:eventId',

  /** Drop Follow-Ups routes */
  DROP_FOLLOW_UPS: '/app/follow-ups',
  /** Follow-up detail view - Parameters: followUpId (number) */
  DROP_FOLLOW_UP_DETAIL: '/app/follow-ups/:followUpId',

  /** Weekly Reports routes */
  WEEKLY_DROP_REPORT: '/app/reports/drop-report',
  WEEKLY_CALL_STATUS: '/app/reports/call-status',

  /** Admin - Field Management */
  FIELD_MANAGEMENT: '/app/admin/field-management',

  /** Notes Module routes */
  NOTES: '/app/notes',
  /** Notes dashboard view */
  NOTES_DASHBOARD: '/app/notes/dashboard',
  /** Notes search view with query support */
  NOTES_SEARCH: '/app/notes/search',
  /** Notes templates gallery */
  NOTES_TEMPLATES: '/app/notes/templates',
  /** Notes tasks rollup view */
  NOTES_TASKS: '/app/notes/tasks',
  /** Notes weekly task board */
  NOTES_WEEKLY_BOARD: '/app/notes/weekly-board',
  /** Notes tag filtered view - Parameters: tagId (string) */
  NOTES_TAGS: '/app/notes/tags/:tagId',
  /** Notes orphan pages report */
  NOTES_ORPHANS: '/app/notes/orphans',
  /** Notes graph/network view */
  NOTES_GRAPH: '/app/notes/graph',

  // Hierarchical notebook routes (ID-based)
  /** Notes notebook view - Parameters: notebookId (string) */
  NOTES_NOTEBOOK: '/app/notes/notebook/:notebookId',
  /** Notes section view - Parameters: notebookId (string), sectionId (string) */
  NOTES_SECTION: '/app/notes/notebook/:notebookId/section/:sectionId',
  /** Notes page editor - Parameters: notebookId (string), sectionId (string), pageId (string) */
  NOTES_PAGE: '/app/notes/notebook/:notebookId/section/:sectionId/page/:pageId',

  // Alternate page-only route (for direct links)
  /** Notes page direct link - Parameters: pageId (string) */
  NOTES_PAGE_DIRECT: '/app/notes/page/:pageId',

  // Slug-based custom URL routes (human-readable URLs)
  /** Notes notebook view by slug - Parameters: notebookSlug (string) */
  NOTES_NOTEBOOK_SLUG: '/app/notes/n/:notebookSlug',
  /** Notes section view by slug - Parameters: notebookSlug (string), sectionSlug (string) */
  NOTES_SECTION_SLUG: '/app/notes/n/:notebookSlug/:sectionSlug',
  /** Notes page view by slug - Parameters: notebookSlug (string), sectionSlug (string), pageSlug (string) */
  NOTES_PAGE_SLUG: '/app/notes/n/:notebookSlug/:sectionSlug/:pageSlug',

  // Template routes
  /** Notes template detail - Parameters: templateId (string) */
  NOTES_TEMPLATE_DETAIL: '/app/notes/templates/:templateId',
  /** Notes template preview - Parameters: templateId (string) */
  NOTES_TEMPLATE_PREVIEW: '/app/notes/templates/:templateId/preview',

  /** Notifications */
  NOTIFICATIONS: '/notifications',
  GEOCODING_INFO: '/notifications/geocoding-info',

  /** Vibe Remote - Claude Code Session Monitor (standalone) */
  VIBE_REMOTE: '/vibe-remote',
  /** Vibe Remote CLI - PTY-only full-screen terminal */
  VIBE_REMOTE_CLI: '/vibe-remote/cli',
  /** Legacy redirect for backwards compatibility */
  HAPPY: '/happy',

  /** Terminal Connect - CLI authentication approval page */
  TERMINAL_CONNECT: '/terminal/connect',

  /** CLI Dashboard - Remote CLI Session Management */
  CLI_DASHBOARD: '/cli-dashboard',

  /** PUG Records - Real-time sync with PUG desktop */
  PUG_RECORDS: '/app/pug-records',

  /** Expenses - Receipt upload with OCR and allocation */
  EXPENSES: '/app/expenses',

  /** Planka - Self-hosted Kanban project board */
  PLANKA: '/app/planka',

  /** Houston Marketing Calendar */
  CALENDAR: '/app/calendar',

  /** HMC Events - Event Report Viewer */
  HMC_EVENTS: '/app/hmc-events',

  /** Client Timelines - Timeline Manager */
  TIMELINES: '/app/timelines',
  /** Timeline Detail View - Parameters: timelineId (number) */
  TIMELINE_DETAIL: '/app/timelines/:timelineId',

  /** Flashcards - Study workspace */
  FLASHCARDS: '/app/flashcards',

  /** Calendar Availability - Outlook FreeBusy Dashboard */
  AVAILABILITY: '/app/availability',

  /** HMC Guest View - Shareable link access (public, no auth) */
  HMC_GUEST: '/hmc-events/guest/:shareId',

  /**
   * Guest Portal Direct Access - for usibenefits-events.eroland.me subdomain
   * When accessed from the subdomain, the token is at the root path: /:shareId
   */
  HMC_GUEST_DIRECT: '/:shareId',

  /** OMNI Report Generator - Underwriting reports for 15 DP opportunities */
  REPORT_GENERATOR: '/app/report-generator',

  /** File Inbox - Email attachments from Gmail polling */
  FILE_INBOX: '/app/file-inbox',

  /** Email Invitations - Mass email campaign manager for cenari.net */
  EMAIL_INVITATIONS: '/app/email-invitations',

  /** USI Events - Event-driven email campaigns with postcard attachments */
  USI_EVENTS: '/app/usi-events',

  /** USI Events Early RSVP - Public form for early event interest (no auth required) */
  EARLY_RSVP: '/early-rsvp',

  /** File Manager - File storage with folders and screenshots */
  FILE_MANAGER: '/app/file-manager',

  /** Weekly Planning Boards - Boards home page */
  BOARDS: '/app/boards',

  /** Opportunities Analyzer - OMNI vs Pipedrive comparison */
  OPPORTUNITIES_ANALYZER: '/app/opportunities-analyzer',

  /** Business Plan - Campaign business plan management (uses selected campaign from store) */
  BUSINESS_PLAN: '/app/business-plan',

  /** Ops Monitor - Live activity dashboard with commit feed and session status */
  CLAWD_BOT: '/app/ops-monitor',

  /** Inbox - Outlook inbox monitoring */
  INBOX: '/app/inbox',
} as const;

/**
 * Map tab names to routes for navigation
 *
 * Provides bidirectional mapping between AppTab types and route paths.
 * Used by AppShell for tab navigation and active tab highlighting.
 *
 * All 12 tabs are mapped (10 primary + Dashboard + EPG + Notes):
 * - Dashboard
 * - Campaign Management
 * - Organization Management
 * - Address Management
 * - Route Planning
 * - Review Routes
 * - Contact Management
 * - Label Generator
 * - Execution Tracking
 * - Handwrytten Export
 * - Event Planning Grid (EPG)
 * - Notes
 */
export const TAB_TO_ROUTE: Record<string, string> = {
  'Dashboard': ROUTES.DASHBOARD,
  'Campaign Management': ROUTES.CAMPAIGNS,
  'Organization Management': ROUTES.ORGANIZATIONS,
  'Address Management': ROUTES.ADDRESSES,
  'Route Planning': ROUTES.ROUTES,
  'Review Routes': ROUTES.REVIEW_ROUTES,
  'Contact Management': ROUTES.CONTACTS,
  'Label Generator': ROUTES.LABELS,
  'Execution Tracking': ROUTES.EXECUTION,
  'Handwrytten Export': ROUTES.HANDWRYTTEN,
  'Event Planning Grid': ROUTES.EPG,
  'Timelines': ROUTES.TIMELINES,
  'Notes': ROUTES.NOTES,
  'Reports': ROUTES.WEEKLY_DROP_REPORT,
  'Report Generator': ROUTES.REPORT_GENERATOR,
};

/**
 * Map routes to tab names for highlighting active tab
 *
 * Reverse mapping used by getActiveTab() in AppShell to determine
 * which tab should be highlighted based on the current route.
 *
 * Note: This is automatically derived from TAB_TO_ROUTE but defined
 * explicitly for performance (no runtime Object.fromEntries()).
 */
export const ROUTE_TO_TAB: Record<string, string> = {
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.CAMPAIGNS]: 'Campaign Management',
  [ROUTES.ORGANIZATIONS]: 'Organization Management',
  [ROUTES.ADDRESSES]: 'Address Management',
  [ROUTES.ROUTES]: 'Route Planning',
  [ROUTES.REVIEW_ROUTES]: 'Review Routes',
  [ROUTES.CONTACTS]: 'Contact Management',
  [ROUTES.LABELS]: 'Label Generator',
  [ROUTES.EXECUTION]: 'Execution Tracking',
  [ROUTES.HANDWRYTTEN]: 'Handwrytten Export',
  [ROUTES.EPG]: 'Event Planning Grid',
  [ROUTES.TIMELINES]: 'Timelines',
  [ROUTES.NOTES]: 'Notes',
  [ROUTES.WEEKLY_DROP_REPORT]: 'Reports',
  [ROUTES.WEEKLY_CALL_STATUS]: 'Reports',
  [ROUTES.REPORT_GENERATOR]: 'Report Generator',
};

// Loading fallback for lazy-loaded views
const LoadingFallback: React.FC = () => (
  <div className="h-full w-full flex flex-col items-center justify-center text-dark-muted space-y-4">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
    <p className="text-sm">Loading view...</p>
  </div>
);

// Wrapper to add Suspense and ErrorBoundary to lazy components
const withSuspense = (Component: React.LazyExoticComponent<React.FC<any>>) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

/**
 * Check if skip-auth is enabled for local development
 * Multiple safeguards ensure this NEVER works in production
 */
const shouldSkipAuth = (): boolean => {
  // PRODUCTION SAFEGUARD 1: Never skip auth on production domain
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'eroland.me' || hostname.endsWith('.eroland.me')) {
      console.warn('[SkipAuth] BLOCKED: Production domain detected');
      return false;
    }
  }

  // PRODUCTION SAFEGUARD 2: Never skip auth in production build
  if (import.meta.env.PROD) {
    return false;
  }

  // Environment variable (for MCP tools - Puppeteer, Playwright, Skyvern)
  if (import.meta.env.VITE_DEV_SKIP_AUTH === 'true') {
    return true;
  }

  // localStorage (for manual developer toggle via console)
  try {
    const stored = localStorage.getItem('bo360_developer_settings');
    if (stored) {
      const settings = JSON.parse(stored);
      return settings.enabled && settings.skipAuth;
    }
  } catch {}

  return false;
};

/**
 * Wrapper component for home route that handles authentication redirects
 *
 * Priority order:
 * 1. If user is authenticated (Remember Me), redirect to /app/campaigns
 * 2. If skip-auth is enabled (dev mode), redirect to /app/organizations
 * 3. If landing page disabled, show LoginPage
 * 4. Otherwise, show LandingPage
 *
 * Uses useEffect to prevent redirect loops with React 18 Strict Mode
 */
const SkipAuthRedirect: React.FC = () => {
  const [shouldRedirect, setShouldRedirect] = React.useState<boolean | null>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // PRIORITY 0A: Events subdomain (events.eroland.me) - handle Early RSVP portal
  // This check happens synchronously before any async auth checks
  if (isEventsSubdomain()) {
    console.log('[EventsPortal] Subdomain detected - showing events portal');
    return <EventsPortalHandler />;
  }

  // PRIORITY 0B: Guest portal subdomain (usibenefits-events.eroland.me) - handle HMC guest view
  // This check happens synchronously before any async auth checks
  if (isGuestPortalSubdomain()) {
    console.log('[GuestPortal] Subdomain detected - showing guest portal root');
    return <GuestPortalRoot />;
  }

  React.useEffect(() => {
    // Timeout fallback - if auth check takes too long, default to landing page
    const timeout = setTimeout(() => {
      if (shouldRedirect === null) {
        console.warn('[SkipAuthRedirect] Timeout - defaulting to landing page');
        setShouldRedirect(false);
      }
    }, 2000);

    try {
      // Only check once after mount to prevent Strict Mode double-render issues
      const skip = shouldSkipAuth();
      console.log('[SkipAuthRedirect] Skip auth check:', skip);
      setShouldRedirect(skip);
    } catch (error) {
      console.error('[SkipAuthRedirect] Error checking skip auth:', error);
      setShouldRedirect(false); // Default to normal flow on error
    }

    return () => clearTimeout(timeout);
  }, []);

  // Show loading spinner while determining redirect state (instead of blank)
  if (shouldRedirect === null) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-dark-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  // PRIORITY 1: If user is authenticated (Remember Me), redirect to dashboard
  if (isAuthenticated) {
    console.log('[Auth] User authenticated - redirecting to /app/dashboard');
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // PRIORITY 2: Skip auth (dev mode only)
  if (shouldRedirect) {
    console.log('[SkipAuth] Redirecting to /app/organizations');
    return <Navigate to={ROUTES.ORGANIZATIONS} replace />;
  }

  // PRIORITY 3: Check landing page config - if disabled, show LoginPage directly
  if (!landingPageConfig.enabled) {
    console.log('[LandingPage] Config disabled - showing LoginPage');
    return <LoginPage />;
  }

  // PRIORITY 4: Show landing page
  return <LandingPage />;
};

/**
 * LoginPage wrapper that redirects authenticated users to dashboard
 */
const LoginPageWrapper: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    console.log('[Login] User already authenticated - redirecting to /app/dashboard');
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <LoginPage />;
};

/**
 * RequireAuth wrapper for standalone protected routes (not in AppShell)
 *
 * Redirects unauthenticated users to login with returnUrl query param.
 * Used for routes like /vibe-remote that need auth but aren't in the main app layout.
 */
interface RequireAuthProps {
  children: React.ReactNode;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const selfHostedAuthenticated = useSelfHostedAuthStore((state) => state.isAuthenticated());
  const demoMode = useSelfHostedAuthStore((state) => state.demoMode);
  const [cookieAuthenticated, setCookieAuthenticated] = useState(() => hasAuthCookies());

  // Cookies are set by the daemon outside React/Zustand, so poll + listen for visibility changes
  useEffect(() => {
    const checkAuthCookies = () => setCookieAuthenticated(hasAuthCookies());

    checkAuthCookies();
    window.addEventListener('focus', checkAuthCookies);
    document.addEventListener('visibilitychange', checkAuthCookies);
    const intervalId = window.setInterval(checkAuthCookies, 5000);

    return () => {
      window.removeEventListener('focus', checkAuthCookies);
      document.removeEventListener('visibilitychange', checkAuthCookies);
      window.clearInterval(intervalId);
    };
  }, []);

  // Check skip-auth for development mode
  const skipAuth = shouldSkipAuth();

  // Self-hosted Happy access (daemon pairing or demo mode) also authorizes standalone routes
  const hasHappyAccess = selfHostedAuthenticated || demoMode || cookieAuthenticated;

  if (!isAuthenticated && !hasHappyAccess && !skipAuth) {
    // Get current path for redirect after login
    const currentPath = window.location.pathname;
    const returnUrl = encodeURIComponent(currentPath);

    console.log('[RequireAuth] User not authenticated - redirecting to login with returnUrl:', currentPath);
    return <Navigate to={`${ROUTES.LOGIN}?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};

/**
 * BoardsHome wrapper that provides navigation callbacks
 *
 * Wraps the BoardsHome component and provides callbacks for:
 * - Selecting a board (navigates to dashboard with board selected)
 * - Creating a weekly board (shows NewWeekDialog)
 * - Creating a standalone board (shows NewBoardDialog)
 */
const BoardsHomeWrapper: React.FC = () => {
  const [showNewWeekDialog, setShowNewWeekDialog] = React.useState(false);
  const [showNewBoardDialog, setShowNewBoardDialog] = React.useState(false);

  const handleSelectBoard = React.useCallback((boardId: string) => {
    // Navigate to dashboard with the board selected
    // The WeeklyBoard component in dashboard will handle loading this board
    window.location.href = `${ROUTES.DASHBOARD}?board=${boardId}`;
  }, []);

  const handleCreateWeeklyBoard = React.useCallback(() => {
    setShowNewWeekDialog(true);
  }, []);

  const handleCreateBoard = React.useCallback(() => {
    setShowNewBoardDialog(true);
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <BoardsHomePage
          onSelectBoard={handleSelectBoard}
          onCreateWeeklyBoard={handleCreateWeeklyBoard}
          onCreateBoard={handleCreateBoard}
        />
        {/* Dialogs would be rendered here - for now just console.log */}
        {showNewWeekDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-dark-bg p-6 rounded-lg">
              <p className="text-white mb-4">New Week Dialog - Coming Soon</p>
              <button
                onClick={() => setShowNewWeekDialog(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {showNewBoardDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-dark-bg p-6 rounded-lg">
              <p className="text-white mb-4">New Board Dialog - Coming Soon</p>
              <button
                onClick={() => setShowNewBoardDialog(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Catch-all handler that checks for portal subdomains
 *
 * On usibenefits-events.eroland.me:
 * - Any unmatched path is treated as a share token
 * - Renders HMCGuestPage which validates the token via API
 *
 * On events.eroland.me:
 * - Routes to EventsPortalHandler for Early RSVP
 *
 * On main domain (eroland.me):
 * - Redirect to home page
 */
const GuestSubdomainCatchAll: React.FC = () => {
  // Handle events subdomain
  if (isEventsSubdomain()) {
    return <EventsPortalHandler />;
  }

  // Handle HMC guest subdomain
  if (isGuestPortalSubdomain()) {
    // On guest subdomain - render guest page (it will extract shareId from URL)
    return <GuestPortalDirect />;
  }
  // Not on any portal subdomain - redirect to home
  return <Navigate to={ROUTES.HOME} replace />;
};

// Route configuration
export const routeConfig: RouteObject[] = [
  // Public routes
  {
    path: ROUTES.HOME,
    element: <SkipAuthRedirect />,
  },
  {
    path: ROUTES.LOGIN,
    element: <LoginPageWrapper />,
  },
  {
    path: ROUTES.PRICING,
    element: <PricingPage />,
  },

  // Notifications pages - standalone outside AppShell for full-page view
  {
    path: '/notifications',
    element: withSuspense(NotificationsPage),
  },
  {
    path: '/notifications/geocoding-info',
    element: withSuspense(GeocodingInfoPage),
  },

  // Vibe Remote - Claude Code Session Monitor (standalone, auth required)
  {
    path: ROUTES.VIBE_REMOTE,
    element: <RequireAuth>{withSuspense(HappyDashboard)}</RequireAuth>,
  },
  // Vibe Remote CLI - PTY-only full-screen terminal (auth required)
  {
    path: ROUTES.VIBE_REMOTE_CLI,
    element: <RequireAuth>{withSuspense(PTYOnlyView)}</RequireAuth>,
  },
  // Legacy redirect: /happy -> /vibe-remote
  {
    path: ROUTES.HAPPY,
    element: <Navigate to={ROUTES.VIBE_REMOTE} replace />,
  },

  // Terminal Connect - CLI authentication approval page (auth required)
  {
    path: ROUTES.TERMINAL_CONNECT,
    element: <RequireAuth>{withSuspense(TerminalConnect)}</RequireAuth>,
  },

  // CLI Dashboard - Remote CLI Session Management (standalone, auth required)
  {
    path: ROUTES.CLI_DASHBOARD,
    element: <RequireAuth>{withSuspense(CLIDashboard)}</RequireAuth>,
  },

  // HMC Guest View - Shareable link access (PUBLIC - no auth required)
  // Guests access events via unique share tokens validated by the API
  {
    path: ROUTES.HMC_GUEST,
    element: withSuspense(HMCGuestPage),
  },

  // USI Events Early RSVP - Public form for early event interest (PUBLIC - no auth required)
  // Users access via link in event emails with query params: ?events=123,456&name=Event1&name=Event2
  {
    path: ROUTES.EARLY_RSVP,
    element: withSuspense(EarlyRSVPPage),
  },

  // Authenticated app routes (wrapped in AppShell)
  {
    path: ROUTES.APP,
    element: <AppShell />,
    children: [
      // Default redirect to dashboard
      {
        index: true,
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },

      // Dashboard (no nested routes - single view)
      {
        path: 'dashboard',
        element: withSuspense(DashboardView),
      },

      // Campaign Management - nested routes
      {
        path: 'campaigns',
        children: [
          {
            index: true,
            element: withSuspense(CampaignManagementView),
          },
          // Phase 3: Detail view
          { path: ':campaignId', element: withSuspense(CampaignDetailView) },
          // Business Plan view
          { path: ':campaignId/business-plan', element: withSuspense(BusinessPlanView) },
          // TODO: Add edit view later
          // { path: ':campaignId/edit', element: withSuspense(CampaignEditView) },
        ],
      },

      // Organization Management - nested routes
      {
        path: 'organizations',
        children: [
          {
            index: true,
            element: withSuspense(OrganizationManagementView),
          },
          // Phase 3: Detail view
          { path: ':organizationId', element: withSuspense(OrganizationDetailView) },
          // TODO: Add edit view later
          // { path: ':organizationId/edit', element: withSuspense(OrganizationEditView) },
        ],
      },

      // Address Management - nested routes
      {
        path: 'addresses',
        children: [
          {
            index: true,
            element: withSuspense(AddressManagementView),
          },
          // Phase 3: Detail view
          { path: ':addressId', element: withSuspense(AddressDetailView) },
        ],
      },

      // Route Planning - nested routes
      {
        path: 'routes',
        children: [
          {
            index: true,
            element: withSuspense(RouteView),
          },
          // Phase 3: Detail view
          { path: ':routeId', element: withSuspense(RouteDetailView) },
          // TODO: Add edit and stop detail views later
          // { path: ':routeId/edit', element: withSuspense(RouteEditView) },
          // { path: ':routeId/stops/:stopId', element: withSuspense(RouteStopDetailView) },
        ],
      },

      // Review Routes - nested routes
      {
        path: 'review-routes',
        children: [
          {
            index: true,
            element: withSuspense(ReviewRoutesView),
          },
        ],
      },

      // Contact Management - nested routes
      {
        path: 'contacts',
        children: [
          {
            index: true,
            element: withSuspense(ContactManagementView),
          },
          // Phase 3: Detail view
          { path: ':contactId', element: withSuspense(ContactDetailView) },
        ],
      },

      // Label Generator (no nested routes - single view)
      {
        path: 'labels',
        element: withSuspense(LabelGeneratorView),
      },

      // Execution Tracking - nested routes
      {
        path: 'execution',
        children: [
          {
            index: true,
            element: withSuspense(ExecutionTrackingView),
          },
          // TODO: Add in Phase 3
          // { path: ':executionId', element: withSuspense(ExecutionDetailView) },
        ],
      },

      // Handwrytten Export (no nested routes - single view)
      {
        path: 'handwrytten',
        element: withSuspense(HandwryttenExportView),
      },

      // Event Planning Grid (EPG) - nested routes
      {
        path: 'epg',
        children: [
          {
            index: true,
            element: withSuspense(EPGPage),
          },
          // Phase 3: Calendar and event detail views
          { path: 'events/:eventId', element: withSuspense(EPGEventDetailView) },
          { path: ':year/:month', element: withSuspense(EPGCalendarView) },
        ],
      },

      // PUG Records - Real-time sync with PUG desktop
      {
        path: 'pug-records',
        element: withSuspense(PugRecordsPage),
      },

      // Expenses - Receipt upload with OCR
      {
        path: 'expenses',
        element: withSuspense(ExpensesPage),
      },

      // Planka - Self-hosted Kanban board
      {
        path: 'planka',
        element: withSuspense(PlankaEmbedView),
      },

      // Houston Marketing Calendar
      {
        path: 'calendar',
        element: withSuspense(CalendarPage),
      },

      // HMC Events - Event Report Viewer
      {
        path: 'hmc-events',
        element: withSuspense(HMCEventsPage),
      },

      // Client Timelines - Timeline Manager
      {
        path: 'timelines',
        children: [
          { index: true, element: withSuspense(TimelinesPage) },
          { path: ':timelineId', element: withSuspense(TimelinesPage) },
        ],
      },

      // Flashcards - Study workspace
      {
        path: 'flashcards',
        element: withSuspense(FlashcardsPage),
      },

      // Calendar Availability - Outlook FreeBusy Dashboard
      {
        path: 'availability',
        element: withSuspense(AvailabilityView),
      },

      // OMNI Report Generator - Underwriting reports for 15 DP opportunities
      {
        path: 'report-generator',
        element: withSuspense(ReportGeneratorView),
      },

      // File Inbox - Email attachments from Gmail polling
      {
        path: 'file-inbox',
        element: withSuspense(FileInboxPage),
      },

      // Email Invitations - Mass email campaign manager for cenari.net
      {
        path: 'email-invitations',
        element: withSuspense(EmailInvitationsPage),
      },

      // USI Events - Event-driven email campaigns with postcard attachments
      {
        path: 'usi-events',
        element: withSuspense(USIEventsPage),
      },

      // File Manager - File storage with folders and screenshots
      {
        path: 'file-manager',
        element: withSuspense(FileManagerPage),
      },

      // Weekly Planning Boards - landing page with board categories
      {
        path: 'boards',
        element: <BoardsHomeWrapper />,
      },

      // Opportunities Analyzer - OMNI vs Pipedrive comparison
      {
        path: 'opportunities-analyzer',
        element: withSuspense(OpportunitiesAnalyzerPage),
      },

      // Business Plan - Campaign business plan management
      {
        path: 'business-plan',
        element: withSuspense(BusinessPlanView),
      },

      // Ops Monitor - Live activity dashboard
      {
        path: 'ops-monitor',
        element: withSuspense(ClawdBotDashboard),
      },

      // Inbox - Outlook inbox monitoring
      {
        path: 'inbox',
        element: withSuspense(InboxPage),
      },

      // Notes - nested routes
      {
        path: 'notes',
        children: [
          {
            index: true,
            element: withSuspense(NotesContent),
          },
          // Dashboard
          { path: 'dashboard', element: withSuspense(NotesContent) },
          // Search
          { path: 'search', element: withSuspense(NotesContent) },
          // Templates
          { path: 'templates', element: withSuspense(NotesContent) },
          { path: 'templates/:templateId', element: withSuspense(NotesContent) },
          { path: 'templates/:templateId/preview', element: withSuspense(NotesContent) },
          // Tasks, Tags, Orphans, Graph
          { path: 'tasks', element: withSuspense(NotesContent) },
          { path: 'weekly-board', element: withSuspense(WeeklyTaskBoard) },
          { path: 'tags/:tagId', element: withSuspense(NotesContent) },
          { path: 'orphans', element: withSuspense(NotesContent) },
          { path: 'graph', element: withSuspense(NotesContent) },
          // Hierarchical navigation (ID-based)
          { path: 'notebook/:notebookId', element: withSuspense(NotesContent) },
          { path: 'notebook/:notebookId/section/:sectionId', element: withSuspense(NotesContent) },
          { path: 'notebook/:notebookId/section/:sectionId/page/:pageId', element: withSuspense(NotesContent) },
          // Direct page link (ID-based)
          { path: 'page/:pageId', element: withSuspense(NotesContent) },
          // Slug-based custom URL routes (human-readable)
          { path: 'n/:notebookSlug', element: withSuspense(NotesContent) },
          { path: 'n/:notebookSlug/:sectionSlug', element: withSuspense(NotesContent) },
          { path: 'n/:notebookSlug/:sectionSlug/:pageSlug', element: withSuspense(NotesContent) },
        ],
      },

      // Drop Follow-Ups
      {
        path: 'follow-ups',
        element: withSuspense(DropFollowUpsView),
      },

      // Weekly Reports - nested routes
      {
        path: 'reports',
        children: [
          { path: 'drop-report', element: withSuspense(WeeklyDropReportView) },
          { path: 'call-status', element: withSuspense(WeeklyCallStatusView) },
        ],
      },

      // Admin - nested routes
      {
        path: 'admin',
        children: [
          { path: 'field-management', element: withSuspense(FieldManagementLayout) },
        ],
      },
    ],
  },

  // Catch-all: On guest subdomain, treat path as share token; otherwise redirect to home
  {
    path: '*',
    element: <GuestSubdomainCatchAll />,
  },
];

// Create the router instance
export const router = createBrowserRouter(routeConfig);

/**
 * Route builder helper functions for type-safe URL construction
 *
 * These helpers ensure correct parameter types and prevent runtime errors
 * from missing or incorrectly typed route parameters.
 */

/**
 * Build campaign detail URL
 * @param campaignId - Campaign ID
 * @example buildCampaignDetailUrl(123) // '/app/campaigns/123'
 */
export const buildCampaignDetailUrl = (campaignId: number): string => {
  return ROUTES.CAMPAIGN_DETAIL.replace(':campaignId', String(campaignId));
};

/**
 * Build campaign edit URL
 * @param campaignId - Campaign ID
 * @example buildCampaignEditUrl(123) // '/app/campaigns/123/edit'
 */
export const buildCampaignEditUrl = (campaignId: number): string => {
  return ROUTES.CAMPAIGN_EDIT.replace(':campaignId', String(campaignId));
};

/**
 * Build campaign business plan URL
 * @param campaignId - Campaign ID
 * @example buildCampaignBusinessPlanUrl(123) // '/app/campaigns/123/business-plan'
 */
export const buildCampaignBusinessPlanUrl = (campaignId: number): string => {
  return ROUTES.CAMPAIGN_BUSINESS_PLAN.replace(':campaignId', String(campaignId));
};

/**
 * Build organization detail URL
 * @param organizationId - Organization ID
 * @example buildOrganizationDetailUrl(456) // '/app/organizations/456'
 */
export const buildOrganizationDetailUrl = (organizationId: number): string => {
  return ROUTES.ORGANIZATION_DETAIL.replace(':organizationId', String(organizationId));
};

/**
 * Build organization edit URL
 * @param organizationId - Organization ID
 * @example buildOrganizationEditUrl(456) // '/app/organizations/456/edit'
 */
export const buildOrganizationEditUrl = (organizationId: number): string => {
  return ROUTES.ORGANIZATION_EDIT.replace(':organizationId', String(organizationId));
};

/**
 * Build address detail URL
 * @param addressId - Address ID
 * @example buildAddressDetailUrl(789) // '/app/addresses/789'
 */
export const buildAddressDetailUrl = (addressId: number): string => {
  return ROUTES.ADDRESS_DETAIL.replace(':addressId', String(addressId));
};

/**
 * Build route detail URL
 * @param routeId - Route ID
 * @example buildRouteDetailUrl('abc123') // '/app/routes/abc123'
 */
export const buildRouteDetailUrl = (routeId: string): string => {
  return ROUTES.ROUTE_DETAIL.replace(':routeId', encodeURIComponent(routeId));
};

/**
 * Build route edit URL
 * @param routeId - Route ID
 * @example buildRouteEditUrl('abc123') // '/app/routes/abc123/edit'
 */
export const buildRouteEditUrl = (routeId: string): string => {
  return ROUTES.ROUTE_EDIT.replace(':routeId', encodeURIComponent(routeId));
};

/**
 * Build route stop detail URL
 * @param routeId - Route ID
 * @param stopId - Stop ID
 * @example buildRouteStopDetailUrl('abc123', 5) // '/app/routes/abc123/stops/5'
 */
export const buildRouteStopDetailUrl = (routeId: string, stopId: number): string => {
  return ROUTES.ROUTE_STOP_DETAIL
    .replace(':routeId', encodeURIComponent(routeId))
    .replace(':stopId', String(stopId));
};

/**
 * Build contact detail URL
 * @param contactId - Contact ID
 * @example buildContactDetailUrl(321) // '/app/contacts/321'
 */
export const buildContactDetailUrl = (contactId: number): string => {
  return ROUTES.CONTACT_DETAIL.replace(':contactId', String(contactId));
};

/**
 * Build execution detail URL
 * @param executionId - Execution ID
 * @example buildExecutionDetailUrl(654) // '/app/execution/654'
 */
export const buildExecutionDetailUrl = (executionId: number): string => {
  return ROUTES.EXECUTION_DETAIL.replace(':executionId', String(executionId));
};

/**
 * Build EPG event detail URL
 * @param eventId - Event ID
 * @example buildEPGEventDetailUrl(987) // '/app/epg/events/987'
 */
export const buildEPGEventDetailUrl = (eventId: number): string => {
  return ROUTES.EPG_EVENT_DETAIL.replace(':eventId', String(eventId));
};

/**
 * Build follow-up detail URL
 * @param followUpId - Follow-up ID
 * @example buildFollowUpDetailUrl(111) // '/app/follow-ups/111'
 */
export const buildFollowUpDetailUrl = (followUpId: number): string => {
  return ROUTES.DROP_FOLLOW_UP_DETAIL.replace(':followUpId', String(followUpId));
};

/**
 * Build HMC guest view URL for shareable links
 * @param shareId - Share token from generated link
 * @example buildHMCGuestUrl('abc123def456...') // '/hmc-events/guest/abc123def456...'
 */
export const buildHMCGuestUrl = (shareId: string): string => {
  return ROUTES.HMC_GUEST.replace(':shareId', encodeURIComponent(shareId));
};

/**
 * Build note detail URL
 * @param noteId - Note ID
 * @example buildNoteDetailUrl(222) // '/app/notes/222'
 */
export const buildNoteDetailUrl = (noteId: number): string => {
  return ROUTES.NOTE_DETAIL.replace(':noteId', String(noteId));
};

/**
 * Build EPG calendar URL
 * @param year - Year (4-digit)
 * @param month - Month (1-12)
 * @example buildEPGCalendarUrl(2025, 3) // '/app/epg/2025/3'
 */
export const buildEPGCalendarUrl = (year: number, month: number): string => {
  return ROUTES.EPG_CALENDAR
    .replace(':year', String(year))
    .replace(':month', String(month));
};

/**
 * Build timeline detail URL
 * @param timelineId - Timeline ID
 * @example buildTimelineDetailUrl(123) // '/app/timelines/123'
 */
export const buildTimelineDetailUrl = (timelineId: number): string => {
  return ROUTES.TIMELINE_DETAIL.replace(':timelineId', String(timelineId));
};

/**
 * Notes route builder functions for hierarchical navigation
 */
export const buildNotesRoute = {
  /** Build dashboard URL */
  dashboard: (): string => ROUTES.NOTES_DASHBOARD,

  /** Build search URL with optional query
   * @param query - Optional search query string
   * @example buildNotesRoute.search('meeting notes') // '/app/notes/search?q=meeting%20notes'
   */
  search: (query?: string): string =>
    query ? `${ROUTES.NOTES_SEARCH}?q=${encodeURIComponent(query)}` : ROUTES.NOTES_SEARCH,

  /** Build notebook URL (ID-based)
   * @param notebookId - Notebook ID
   * @example buildNotesRoute.notebook('nb-123') // '/app/notes/notebook/nb-123'
   */
  notebook: (notebookId: string): string =>
    ROUTES.NOTES_NOTEBOOK.replace(':notebookId', encodeURIComponent(notebookId)),

  /** Build section URL (ID-based)
   * @param notebookId - Notebook ID
   * @param sectionId - Section ID
   * @example buildNotesRoute.section('nb-123', 'sec-456') // '/app/notes/notebook/nb-123/section/sec-456'
   */
  section: (notebookId: string, sectionId: string): string =>
    ROUTES.NOTES_SECTION
      .replace(':notebookId', encodeURIComponent(notebookId))
      .replace(':sectionId', encodeURIComponent(sectionId)),

  /** Build page URL (ID-based, full hierarchical path)
   * @param notebookId - Notebook ID
   * @param sectionId - Section ID
   * @param pageId - Page ID
   * @example buildNotesRoute.page('nb-123', 'sec-456', 'page-789') // '/app/notes/notebook/nb-123/section/sec-456/page/page-789'
   */
  page: (notebookId: string, sectionId: string, pageId: string): string =>
    ROUTES.NOTES_PAGE
      .replace(':notebookId', encodeURIComponent(notebookId))
      .replace(':sectionId', encodeURIComponent(sectionId))
      .replace(':pageId', encodeURIComponent(pageId)),

  /** Build direct page URL (for shareable links)
   * @param pageId - Page ID
   * @example buildNotesRoute.pageDirect('page-789') // '/app/notes/page/page-789'
   */
  pageDirect: (pageId: string): string =>
    ROUTES.NOTES_PAGE_DIRECT.replace(':pageId', encodeURIComponent(pageId)),

  // =========================================================================
  // Slug-based custom URL routes (human-readable)
  // =========================================================================

  /** Build notebook URL using slug
   * @param notebookSlug - URL-friendly notebook slug
   * @example buildNotesRoute.notebookBySlug('work-notes') // '/app/notes/n/work-notes'
   */
  notebookBySlug: (notebookSlug: string): string =>
    ROUTES.NOTES_NOTEBOOK_SLUG.replace(':notebookSlug', encodeURIComponent(notebookSlug)),

  /** Build section URL using slugs
   * @param notebookSlug - URL-friendly notebook slug
   * @param sectionSlug - URL-friendly section slug
   * @example buildNotesRoute.sectionBySlug('work-notes', 'meetings') // '/app/notes/n/work-notes/meetings'
   */
  sectionBySlug: (notebookSlug: string, sectionSlug: string): string =>
    ROUTES.NOTES_SECTION_SLUG
      .replace(':notebookSlug', encodeURIComponent(notebookSlug))
      .replace(':sectionSlug', encodeURIComponent(sectionSlug)),

  /** Build page URL using slugs (human-readable custom URL)
   * @param notebookSlug - URL-friendly notebook slug
   * @param sectionSlug - URL-friendly section slug
   * @param pageSlug - URL-friendly page slug
   * @example buildNotesRoute.pageBySlug('work-notes', 'meetings', 'standup-notes')
   *          // '/app/notes/n/work-notes/meetings/standup-notes'
   */
  pageBySlug: (notebookSlug: string, sectionSlug: string, pageSlug: string): string =>
    ROUTES.NOTES_PAGE_SLUG
      .replace(':notebookSlug', encodeURIComponent(notebookSlug))
      .replace(':sectionSlug', encodeURIComponent(sectionSlug))
      .replace(':pageSlug', encodeURIComponent(pageSlug)),

  /** Build template detail URL
   * @param templateId - Template ID
   * @example buildNotesRoute.template('tmpl-123') // '/app/notes/templates/tmpl-123'
   */
  template: (templateId: string): string =>
    ROUTES.NOTES_TEMPLATE_DETAIL.replace(':templateId', encodeURIComponent(templateId)),

  /** Build template preview URL
   * @param templateId - Template ID
   * @example buildNotesRoute.templatePreview('tmpl-123') // '/app/notes/templates/tmpl-123/preview'
   */
  templatePreview: (templateId: string): string =>
    ROUTES.NOTES_TEMPLATE_PREVIEW.replace(':templateId', encodeURIComponent(templateId)),

  /** Build tag filtered view URL
   * @param tagId - Tag ID
   * @example buildNotesRoute.tag('work') // '/app/notes/tags/work'
   */
  tag: (tagId: string): string =>
    ROUTES.NOTES_TAGS.replace(':tagId', encodeURIComponent(tagId)),

  /** Build tasks rollup URL */
  tasks: (): string => ROUTES.NOTES_TASKS,

  /** Build weekly task board URL */
  weeklyBoard: (): string => ROUTES.NOTES_WEEKLY_BOARD,

  /** Build orphans report URL */
  orphans: (): string => ROUTES.NOTES_ORPHANS,

  /** Build graph view URL */
  graph: (): string => ROUTES.NOTES_GRAPH,
};
